import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import { holdBackButton } from "../utils/backButtonGate";

const pathnameOf = (to) => (typeof to === "string" ? to.split(/[?#]/)[0] : to?.pathname);

/** A `GratsMeetEmbed` status in which this host is holding a class open. */
export const inGratsClass = (status) => status === "in-meeting" || status === "reconnecting";

/**
 * useMeetingLeaveGuard — asks before a host walks out of a live Grats Meet class.
 *
 * A Grats Meet recording runs in the host's browser, so leaving the page stops
 * it while the class carries on without them. Three ways out, and only the
 * first can be caught by the browser:
 *   1. Closing or reloading the tab: `beforeunload`, the browser's own prompt.
 *      Clicking inside the meeting frame counts as using the page, which is
 *      what the browser asks for before it will show one.
 *   2. Any link or `navigate()` inside the app: never unloads the page, so the
 *      browser says nothing. App.js uses BrowserRouter, which has no `useBlocker`,
 *      so the router's own navigator is held for as long as the guard is on.
 *   3. The browser's Back button: the address has already changed by the time
 *      anyone hears of it, so `backButtonGate` stops the router being told and
 *      the class goes back in the address bar.
 *
 * `active` must stay false for every other provider: Zoom ends a class by
 * redirecting the page, and a prompt there traps the trainer.
 *
 *   const guard = useMeetingLeaveGuard(inGratsClass(status));
 *   <MeetingLeaveConfirm guard={guard} />
 *
 * Call `guard.release()` before sending the page away yourself. It takes effect
 * at once, where turning `active` off would wait for the next render.
 */
export default function useMeetingLeaveGuard(active) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const [pendingLeave, setPendingLeave] = useState(null);
  const releaseRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;

    const { push, replace, go } = navigator;
    const classUrl = window.location.href;
    const classState = window.history.state;
    const ask = (proceed) => setPendingLeave(() => proceed);
    const samePage = (to) => pathnameOf(to) === window.location.pathname;
    const guarded = (send) => (to, ...rest) => {
      if (samePage(to)) send(to, ...rest);
      else ask(() => send(to, ...rest));
    };

    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const onBack = () => {
      window.history.pushState(classState, "", classUrl);
      ask(() => go(-1));
    };

    navigator.push = guarded(push);
    navigator.replace = guarded(replace);
    navigator.go = (delta) => ask(() => go(delta));
    window.addEventListener("beforeunload", onBeforeUnload);
    const letBackGo = holdBackButton(onBack);

    const release = () => {
      Object.assign(navigator, { push, replace, go });
      window.removeEventListener("beforeunload", onBeforeUnload);
      letBackGo();
      releaseRef.current = null;
    };
    releaseRef.current = release;
    return release;
  }, [active, navigator]);

  const release = useCallback(() => {
    if (releaseRef.current) releaseRef.current();
  }, []);

  const stay = useCallback(() => setPendingLeave(null), []);

  const leave = useCallback(() => {
    release();
    setPendingLeave(null);
    if (pendingLeave) pendingLeave();
  }, [pendingLeave, release]);

  return { asking: !!pendingLeave, stay, leave, release };
}
